import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Link,
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useBrand } from "@webuild-ai/react-auth";

import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { MarkdownWeBuild } from "@/components/MarkdownWeBuild";
import { FileTypeIcon } from "@/components/UI/FileTypeIcon";

interface SourceFile {
  id: string;
  title: string;
  summary: string;
  icon: string;
  timestamp: string;
  uploaded_by: string;
}

interface SourceWeb {
  title: string;
  summary: string;
  favicon: string;
  url: string;
}

interface MessageSourcesProps {
  sourcesFiles: SourceFile[];
  sourcesWeb: SourceWeb[];
}

const MessageSources: React.FC<MessageSourcesProps> = ({ sourcesFiles = [], sourcesWeb = [] }) => {
  if (sourcesFiles.length === 0 && sourcesWeb.length === 0) {
    return <></>;
  }

  const [showAllSources, setShowAllSources] = useState(false);
  const [visibleSources, setVisibleSources] = useState<number>(3);
  const [expandedSummaries, setExpandedSummaries] = useState<{
    [key: number]: boolean;
  }>({});
  const { platformClient } = useWebuildAPIClient();
  const { brand } = useBrand();

  const downloadFile = async (fileId: string) => {
    if (!platformClient) return;

    const response = await platformClient.files.createTemporaryDownloadUrl({
      fileId: fileId,
    });

    if (response && response.temporary_url) {
      window.open(`https://api.${window.location.hostname}${response.temporary_url}`, "_blank");
    }
  };

  const { t } = useTranslation();

  useEffect(() => {
    // Adjust number of visible sources based on screen size
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 640) {
        // xs
        setVisibleSources(1);
      } else if (width < 768) {
        // sm
        setVisibleSources(2);
      } else if (width < 1024) {
        // md
        setVisibleSources(3);
      } else {
        // lg and above
        setVisibleSources(4);
      }
    };

    handleResize(); // Set initial value
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleSourcesView = () => {
    setShowAllSources(!showAllSources);
  };

  const toggleSummary = (index: number) => {
    setExpandedSummaries((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const hiddenSourcesCount = sourcesFiles.length + sourcesWeb.length - visibleSources;

  const renderSummary = (summary: string, index: number, maxLength: number = 180) => {
    const isExpanded = expandedSummaries[index];
    const shouldShowToggle = summary.length > maxLength;

    if (!shouldShowToggle) {
      return (
        <div className="text-xs">
          <MarkdownWeBuild>{summary}</MarkdownWeBuild>
        </div>
      );
    }

    return (
      <div className="text-xs">
        <MarkdownWeBuild>
          {isExpanded ? summary : `${summary.slice(0, maxLength)}...`}
        </MarkdownWeBuild>
        <Button
          className="p-0 min-w-0 h-auto text-xs"
          size="sm"
          variant="ghost"
          onPress={() => toggleSummary(index)}
        >
          {isExpanded ? "Show less" : "Show more"}
        </Button>
      </div>
    );
  };

  const visibleWebSources = Math.max(0, visibleSources - sourcesFiles.length);

  return (
    <div className="mt-4">
      {/** Collapsible sources - Files */}
      {!showAllSources ? (
        <div className="flex flex-row items-stretch gap-4 overflow-x-auto pb-2">
          {sourcesFiles.slice(0, visibleSources).map((source: SourceFile, index: number) => (
            <Card
              key={index}
              className="min-w-[250px] max-w-[300px] flex-shrink-0"
              data-id={`source-card-${index}`}
            >
              <CardHeader className="flex flex-row gap-2">
                <FileTypeIcon height={40} icon={source.icon} style={{ minWidth: "40px" }} />
                <div className="flex flex-col">
                  <span
                    className="text-sm cursor-pointer hover:underline"
                    data-id={`source-title-${index}`}
                    role="button"
                    tabIndex={0}
                    title="Download file"
                    onClick={() => {
                      if ("id" in source && source.id && source.title) {
                        downloadFile(source.id);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if ("id" in source && source.id && source.title) {
                          downloadFile(source.id);
                        }
                      }
                    }}
                  >
                    {source.title}
                  </span>
                  <p className="text-xs text-small-500 truncate">
                    {source.uploaded_by} <br /> {source.timestamp}
                  </p>
                </div>
              </CardHeader>
              <CardContent className="pt-0 gap-top-0">
                {renderSummary(source.summary, index)}
              </CardContent>
            </Card>
          ))}

          {sourcesWeb.slice(0, visibleWebSources).map((source: SourceWeb, index: number) => {
            const hostname = (() => {
              try {
                return new URL(source.url || "").hostname.replace(/^www\./, "");
              } catch {
                return source.url || "";
              }
            })();

            const cardLink = (
              <a
                key={index}
                className="w-[220px] min-w-[220px] max-w-[220px] min-h-[5rem] shrink-0 flex flex-col items-start justify-start gap-1.5 p-2.5 rounded-xl bg-content1 border border-default-200 hover:border-default-300 hover:bg-default-50 no-underline transition-colors"
                data-id={`source-card-${index}`}
                href={source.url || ""}
                rel="noopener noreferrer"
                target="_blank"
              >
                <div className="flex flex-row items-center gap-1.5 min-w-0 w-full">
                  <span
                    className="shrink-0 bg-default-200 overflow-hidden inline-flex items-center justify-center"
                    style={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "9999px",
                      padding: "2px",
                    }}
                  >
                    <img
                      aria-hidden
                      alt=""
                      height={14}
                      src={source.favicon}
                      style={{ width: "100%", height: "100%", objectFit: "contain" }}
                      width={14}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14'%3E%3Crect width='14' height='14' rx='2' fill='%23e5e5e5'/%3E%3C/svg%3E";
                      }}
                    />
                  </span>
                  <span className="text-tiny text-default-500 truncate flex-1 min-w-0 text-left">
                    {hostname}
                  </span>
                </div>
                <span
                  className="text-xs font-medium leading-snug line-clamp-2 text-foreground w-full text-left min-h-[2.75em]"
                  data-id={`source-title-${index}`}
                >
                  {source.title}
                </span>
              </a>
            );

            if (!source.summary) return cardLink;

            return (
              <Tooltip key={index}>
                <TooltipTrigger>{cardLink}</TooltipTrigger>
                <TooltipContent>
                  <div
                    className="max-w-[320px] text-xs leading-relaxed whitespace-normal break-normal hyphens-auto"
                    data-id={`source-summary-${index}`}
                  >
                    {source.summary}
                  </div>
                </TooltipContent>
              </Tooltip>
            );
          })}

          {hiddenSourcesCount > 0 && (
            <Button className="self-center btn-brand-primary" onPress={toggleSourcesView}>
              +{hiddenSourcesCount} sources
            </Button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {sourcesFiles.map((source: SourceFile, index: number) => (
            <div key={index} className="flex flex-row gap-3 p-3 border-b">
              <FileTypeIcon height={40} icon={source.icon} style={{ minWidth: "40px" }} />
              <div className="flex flex-col w-1/10">
                <Link
                  isExternal
                  showAnchorIcon
                  className="text-sm"
                  data-id={`source-title-${index}`}
                  onPress={() => {
                    if ("id" in source && source.id && source.title) {
                      downloadFile(source.id);
                    }
                  }}
                >
                  {source.title}
                </Link>
                <p className="text-xs text-small-500 truncate">
                  {source.uploaded_by} <br /> {source.timestamp}
                </p>
              </div>
              <div className="flex flex-col flex-grow text-sm">
                {renderSummary(source.summary, index, 2000)}
              </div>
            </div>
          ))}

          {sourcesWeb.map((source: SourceWeb, index: number) => {
            const hostname = (() => {
              try {
                return new URL(source.url || "").hostname.replace(/^www\./, "");
              } catch {
                return source.url || "";
              }
            })();

            return (
              <div key={index} className="flex flex-row gap-3 p-3 border-b border-default-200">
                <span
                  className="shrink-0 bg-default-100 self-start mt-0.5 overflow-hidden inline-flex items-center justify-center"
                  style={{ width: "20px", height: "20px", borderRadius: "9999px" }}
                >
                  <img
                    aria-hidden
                    alt=""
                    height={20}
                    src={source.favicon}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                    width={20}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        `/brands/${brand.prefix || "webuild"}/pathfinder.svg`;
                    }}
                  />
                </span>
                <div className="flex flex-col w-[220px] shrink-0 min-w-0">
                  <a
                    className="text-xs font-medium leading-snug text-foreground no-underline hover:underline"
                    data-id={`source-title-${index}`}
                    href={source.url || ""}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <span className="block break-words">{source.title}</span>
                  </a>
                  <span className="text-tiny text-default-500 truncate mt-0.5">{hostname}</span>
                </div>
                <div className="flex flex-col grow text-xs text-default-600 min-w-0">
                  {renderSummary(source.summary, index)}
                </div>
              </div>
            );
          })}

          <Button className="self-start mt-2 btn-brand-primary" onPress={toggleSourcesView}>
            <Icon icon="solar:arrow-left-linear" /> {t("Show less")}
          </Button>
        </div>
      )}
    </div>
  );
};

export { MessageSources, type SourceFile, type SourceWeb };
